/**
 * Case Management Service - Frontend API Client
 * Handles communication with the case management backend API
 */

// API Base URL - configurable for different environments
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v3';

export interface CaseData {
  id: string;
  case_number?: string; // Made optional since backend sometimes returns null
  case_type: string;
  case_sub_type?: string; // Added for unified supplier case
  status: CaseStatus;
  priority: number;
  title?: string; // Made optional since some cases might not have titles
  customer_email: string;
  customer_name?: string; // Added from backend data
  customer_company?: string; // Added from backend data
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  metadata: any;
  workflow_state?: string; // Added for workflow state tracking
}

export interface SupplierCaseData extends CaseData {
  supplier_data?: {
    supplier_id: string;
    related_order_id: string;
    issue_type: string;
    original_quantity?: number;
    proposed_quantity?: number;
    impact_assessment?: {
      severity: 'low' | 'medium' | 'high';
      shortfall_quantity: number;
      shortfall_percentage: number;
      production_impact: 'none' | 'manageable' | 'blocking';
      recommended_action: string;
    };
  };
}

export interface WorkflowStep {
  id: string;
  name: string;
  status: 'completed' | 'current' | 'pending';
}

export interface CaseAction {
  id: string;
  name: string;
  priority: 'low' | 'medium' | 'high';
}

export interface CaseActivity {
  id: string;
  case_id: string;
  activity_type: string;
  description: string;
  performed_by: string;
  created_at: string;
}

export interface CaseDetailsResponse {
  case: SupplierCaseData;
  workflow_steps: WorkflowStep[];
  next_actions: CaseAction[];
  activities: CaseActivity[];
  supplier_data?: any;
}

export type CaseStatus = 'new' | 'open' | 'in_progress' | 'pending_response' | 'resolved' | 'closed';

export interface CaseFilters {
  case_type?: string;
  status?: string;
  assigned_to?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreateSupplierCaseRequest {
  case_type: 'supplier_case';
  case_sub_type?: 'quote_request' | 'order_issue' | 'follow_up';
  customer_email: string; // Supplier email goes here for supplier cases
  customer_name?: string; // Supplier name
  customer_company?: string; // Supplier company
  title: string;
  priority?: number;
  metadata?: any; // Contains order_type, positions, supplier_id, etc.
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  meta?: {
    total?: number;
    limit?: number;
    offset?: number;
  };
}

export class CaseManagementService {
  // Use configurable API base URL like unifiedOrderService
  private baseUrl = API_BASE_URL;

  /**
   * Get all cases with optional filters
   */
  async getCases(filters?: CaseFilters): Promise<ApiResponse<CaseData[]>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }

      const response = await fetch(`${this.baseUrl}/cases?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Laden der Fälle',
          error: result.error
        };
      }

      // The backend returns the cases in result.data, and meta information in result.meta
      return {
        success: result.success,
        message: result.message,
        data: result.data || [],
        meta: result.meta // Include pagination info
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Laden der Fälle',
        error: error.message
      };
    }
  }

  /**
   * Get detailed information about a specific case
   */
  async getCaseDetails(caseId: string): Promise<ApiResponse<CaseDetailsResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Laden der Fall-Details',
          error: result.error
        };
      }

      // Transform the simple case response into the expected CaseDetailsResponse format
      const caseData = result.data;
      const detailsResponse: CaseDetailsResponse = {
        case: {
          ...caseData,
          case_number: caseData.case_number || 'N/A',
          title: caseData.metadata?.title || `Case ${caseData.case_type}`
        },
        workflow_steps: [
          {
            id: 'created',
            name: 'Case Created',
            status: 'completed'
          },
          {
            id: 'in_progress',
            name: 'In Progress',
            status: caseData.status === 'new' ? 'pending' : (caseData.status === 'open' || caseData.status === 'in_progress' ? 'current' : 'completed')
          },
          {
            id: 'resolved',
            name: 'Resolved',
            status: caseData.status === 'resolved' || caseData.status === 'closed' ? 'completed' : 'pending'
          }
        ],
        next_actions: this.getNextActionsForCase(caseData),
        activities: [],
        supplier_data: caseData.metadata || {}
      };

      return {
        success: true,
        message: result.message,
        data: detailsResponse
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Laden der Fall-Details',
        error: error.message
      };
    }
  }

  /**
   * Get appropriate next actions based on case status and type
   */
  private getNextActionsForCase(caseData: any): CaseAction[] {
    const actions: CaseAction[] = [];

    switch (caseData.status) {
      case 'new':
        actions.push(
          { id: 'review', name: 'Review Case', priority: 'high' },
          { id: 'assign', name: 'Assign to Specialist', priority: 'medium' }
        );
        break;
      case 'open':
        actions.push(
          { id: 'progress', name: 'Move to In Progress', priority: 'high' },
          { id: 'contact_customer', name: 'Contact Customer', priority: 'medium' }
        );
        break;
      case 'in_progress':
        actions.push(
          { id: 'resolve', name: 'Mark as Resolved', priority: 'high' },
          { id: 'escalate', name: 'Escalate', priority: 'low' }
        );
        break;
      case 'resolved':
        actions.push(
          { id: 'close', name: 'Close Case', priority: 'medium' },
          { id: 'reopen', name: 'Reopen', priority: 'low' }
        );
        break;
    }

    return actions;
  }

  /**
   * Execute an action on a case (update case status, etc.)
   * Backward compatibility alias for updateCase
   */
  async executeCaseAction(
    caseId: string, 
    action: string, 
    reason?: string
  ): Promise<ApiResponse> {
    // Map common actions to status updates
    const actionToStatusMap: { [key: string]: string } = {
      'accept': 'in_progress',
      'reject': 'closed',
      'approve': 'approved',
      'complete': 'resolved',
      'reopen': 'open'
    };

    const updates: any = {};
    
    if (actionToStatusMap[action]) {
      updates.status = actionToStatusMap[action];
    }
    
    if (reason) {
      updates.metadata = {
        action_reason: reason,
        last_action: action,
        last_action_date: new Date().toISOString()
      };
    }

    return this.updateCase(caseId, updates);
  }

  /**
   * Update case (replaces executeCaseAction with more flexible approach)
   */
  async updateCase(
    caseId: string, 
    updates: Partial<CaseData>
  ): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Aktualisieren des Falls',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Aktualisieren des Falls',
        error: error.message
      };
    }
  }

  /**
   * Create a new case (replaces createSupplierCase)
   */
  async createCase(caseData: any): Promise<ApiResponse<CaseData>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(caseData),
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Erstellen des Falls',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Erstellen des Falls',
        error: error.message
      };
    }
  }

  /**
   * Create customer case with new position-based API
   */
  async createCustomerCase(customerData: {
    customer_email: string;
    customer_name?: string;
    customer_company?: string;
    title?: string;
    description?: string;
    priority?: number;
    quote_deadline?: string;
    positions?: Array<{
      part_name: string;
      part_number?: string;
      material?: string;
      quantity: number;
      specifications?: string;
      dimensional_drawing?: File;
      supplier_quote_type?: string;
      files?: any[];
    }>;
    related_email_id?: string;
  }): Promise<ApiResponse<any>> {
    try {
      console.log('📤 [CREATE-CUSTOMER-CASE] Starting customer case creation with file uploads');
      console.log('📊 [CREATE-CUSTOMER-CASE] Customer data:', {
        customer_email: customerData.customer_email,
        title: customerData.title,
        positions_count: customerData.positions?.length || 0,
        positions_with_files: customerData.positions?.filter(p => p.dimensional_drawing).length || 0
      });

      // Check if we have any files to upload
      const hasFiles = customerData.positions?.some(p => p.dimensional_drawing instanceof File);
      
      if (hasFiles) {
        console.log('📁 [CREATE-CUSTOMER-CASE] Files detected, using FormData approach');
        
        // Create FormData for file uploads
        const formData = new FormData();
        
        // Add basic customer data
        formData.append('customer_email', customerData.customer_email);
        if (customerData.customer_name) formData.append('customer_name', customerData.customer_name);
        if (customerData.customer_company) formData.append('customer_company', customerData.customer_company);
        if (customerData.title) formData.append('title', customerData.title);
        if (customerData.description) formData.append('description', customerData.description);
        if (customerData.priority) formData.append('priority', customerData.priority.toString());
        if (customerData.quote_deadline) formData.append('quote_deadline', customerData.quote_deadline);
        if (customerData.related_email_id) formData.append('related_email_id', customerData.related_email_id);
        
        // Process positions and files
        const positionsData = customerData.positions?.map((pos, index) => {
          const positionData = {
            part_name: pos.part_name,
            part_number: pos.part_number,
            material: pos.material,
            quantity: pos.quantity,
            specifications: pos.specifications,
            supplier_quote_type: pos.supplier_quote_type || 'general'
          };
          
          // If position has a file, add it to FormData
          if (pos.dimensional_drawing instanceof File) {
            formData.append(`position_${index}_file`, pos.dimensional_drawing);
            console.log(`📎 [CREATE-CUSTOMER-CASE] Added file for position ${index}: ${pos.dimensional_drawing.name}`);
          }
          
          return positionData;
        }) || [];
        
        formData.append('positions', JSON.stringify(positionsData));
        
        const response = await fetch(`${this.baseUrl}/cases/customer`, {
          method: 'POST',
          body: formData, // Don't set Content-Type header - let browser set it for FormData
        });

        const result = await response.json();
        
        if (!response.ok) {
          console.error('❌ [CREATE-CUSTOMER-CASE] Error response:', result);
          return {
            success: false,
            message: result.message || 'Fehler beim Erstellen des Kundenfalls',
            error: result.error
          };
        }

        console.log('✅ [CREATE-CUSTOMER-CASE] Customer case created successfully with files');
        return result;
      } else {
        console.log('📄 [CREATE-CUSTOMER-CASE] No files, using JSON approach');
        
        // No files, use regular JSON approach
        const response = await fetch(`${this.baseUrl}/cases/customer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(customerData),
        });

        const result = await response.json();
        
        if (!response.ok) {
          return {
            success: false,
            message: result.message || 'Fehler beim Erstellen des Kundenfalls',
            error: result.error
          };
        }

        return result;
      }
    } catch (error: any) {
      console.error('❌ [CREATE-CUSTOMER-CASE] Exception:', error);
      return {
        success: false,
        message: 'Fehler beim Erstellen des Kundenfalls',
        error: error.message
      };
    }
  }

  /**
   * Add position to customer case
   */
  async addPositionToCase(caseId: string, positionData: {
    part_name: string;
    part_number?: string;
    material?: string;
    quantity: number;
    description?: string;
    supplier_quote_type?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}/positions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(positionData),
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Hinzufügen der Position',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Hinzufügen der Position',
        error: error.message
      };
    }
  }

  /**
   * Get positions for customer case
   */
  async getCasePositions(caseId: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}/positions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Laden der Positionen',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Laden der Positionen',
        error: error.message
      };
    }
  }

  /**
   * PHASE 3: Manual Transfer Interface & Data Organization Methods
   */

  /**
   * Organize customer case data for manual transfer
   */
  async organizeCustomerCaseData(caseId: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}/organize-data`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Organisieren der Daten',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Organisieren der Daten',
        error: error.message
      };
    }
  }

  /**
   * Generate manual transfer summary
   */
  async generateExportSummary(caseId: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}/export-summary`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Erstellen der Zusammenfassung',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Erstellen der Zusammenfassung',
        error: error.message
      };
    }
  }

  /**
   * Update position data for preprocessing
   */
  async updatePosition(positionId: string, updates: {
    part_name?: string;
    part_number?: string;
    quantity?: number;
    description?: string;
    supplier_quote_type?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/positions/${positionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Aktualisieren der Position',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Aktualisieren der Position',
        error: error.message
      };
    }
  }

  /**
   * Mark case as ready for manual export
   */
  async markCaseReadyForExport(caseId: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}/mark-ready-for-export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Markieren für Export',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Markieren für Export',
        error: error.message
      };
    }
  }

  /**
   * Prepare manual export with complete preprocessing
   */
  async prepareManualExport(caseId: string): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}/prepare-manual-export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Vorbereiten des Exports',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Vorbereiten des Exports',
        error: error.message
      };
    }
  }

  /**
   * Create case from email data
   */
  async createCaseFromEmail(emailData: any): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/from-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Erstellen des Falls aus E-Mail',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Erstellen des Falls aus E-Mail',
        error: error.message
      };
    }
  }

  /**
   * Link case to order
   */
  async linkCaseToOrder(caseId: string, orderId: string): Promise<ApiResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/${caseId}/link-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Verknüpfen mit Bestellung',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Verknüpfen mit Bestellung',
        error: error.message
      };
    }
  }

  /**
   * Get cases related to an order
   */
  async getOrderCases(orderId: string): Promise<ApiResponse<CaseData[]>> {
    try {
      const response = await fetch(`${this.baseUrl}/orders/${orderId}/cases`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Laden der Bestellungsfälle',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Laden der Bestellungsfälle',
        error: error.message
      };
    }
  }

  /**
   * Get case analytics
   */
  async getCaseAnalytics(): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/cases/analytics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Laden der Fall-Statistiken',
          error: result.error
        };
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Laden der Fall-Statistiken',
        error: error.message
      };
    }
  }

  /**
   * Create a unified supplier case (quote request, order issue, or follow-up)
   */
  async createSupplierCase(caseData: CreateSupplierCaseRequest): Promise<ApiResponse<CaseData>> {
    try {
      console.log('🔧 createSupplierCase called with case_sub_type:', caseData.case_sub_type);
      
      // For quote requests, use the /quotes/initiate endpoint
      if (caseData.case_sub_type === 'quote_request') {
        console.log('✅ Routing to createQuoteRequest');
        return this.createQuoteRequest(caseData);
      }
      
      console.log('⚠️  Using legacy /cases endpoint for case_sub_type:', caseData.case_sub_type);
      
      // For other case types, use the legacy endpoint (if available)
      const response = await fetch(`${this.baseUrl}/cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(caseData)
      });

      const result = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: result.message || 'Fehler beim Erstellen des Supplier Case',
          error: result.error
        };
      }

      return {
        success: result.success,
        message: result.message || 'Supplier Case erfolgreich erstellt',
        data: result.data
      };
    } catch (error: any) {
      return {
        success: false,
        message: 'Fehler beim Erstellen des Supplier Case',
        error: error.message
      };
    }
  }

  /**
   * Create a quote request using the /quotes/initiate endpoint
   */
  async createQuoteRequest(caseData: CreateSupplierCaseRequest): Promise<ApiResponse<CaseData>> {
    try {
      console.log('\n� ===== CASE MANAGEMENT SERVICE DEBUG =====');
      console.log('�🔧 createQuoteRequest called with caseData:', JSON.stringify(caseData, null, 2));
      
      // First, look up the supplier by email to get the correct ID
      let supplierId = caseData.metadata?.supplier_id || 'MANUAL';
      const supplierEmail = caseData.customer_email; // In frontend, customer_email is actually supplier email
      
      console.log('🔍 Supplier lookup:', {
        initialSupplierId: supplierId,
        supplierEmail,
        hasMetadata: !!caseData.metadata
      });
      
      if (supplierEmail) {
        try {
          console.log('🔍 Looking up supplier by email:', supplierEmail);
          // New: Look up supplier by email
          const response = await fetch(`${this.baseUrl}/suppliers/lookup?email=${encodeURIComponent(supplierEmail)}`, {
            method: 'GET',
            headers: this.getHeaders()
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data?.id) {
              console.log(`✅ Found supplier in database with ID: ${data.data.id}`);
              supplierId = data.data.id;
            } else {
              console.log('⚠️ No supplier found for email, will use provided ID:', supplierEmail);
            }
          } else {
            console.warn('Failed to lookup supplier - API response not ok:', response.status);
          }
        } catch (lookupError) {
          console.warn('Failed to look up supplier, will use provided ID:', lookupError);
        }
      }
      
      // Map the frontend case data to the backend quote request format
      const primarySupplier = {
        email: supplierEmail,
        name: caseData.customer_name || '',
        company: caseData.customer_company || ''
      };

      // NEW: Include all suppliers if available
      const allSuppliers = caseData.metadata?.suppliers || [primarySupplier];
      console.log('📤 [MULTI-SUPPLIER] Including all suppliers:', allSuppliers);

      const quoteRequest = {
        supplier_id: supplierId, // Primary supplier ID for backward compatibility
        supplier_email: primarySupplier.email,
        supplier_name: primarySupplier.name || primarySupplier.company || primarySupplier.email,
        order_type: caseData.metadata?.order_type || 'material',
        positions: caseData.metadata?.positions || [],
        header: {
          title: caseData.title
        },
        quote_deadline: undefined, // Can be added later
        special_instructions: caseData.metadata?.email?.body_text || '',
        initiated_by: 'frontend@wzf.de',
        // NEW: Include all suppliers
        suppliers: allSuppliers
      };

      console.log('\n� ===== BACKEND REQUEST DEBUG =====');
      console.log('�📤 Sending quote request to /quotes/initiate:');
      console.log('🔍 Request payload:', JSON.stringify(quoteRequest, null, 2));
      console.log('📊 Request summary:', {
        supplierCount: allSuppliers.length,
        positionsCount: quoteRequest.positions.length,
        orderType: quoteRequest.order_type,
        primarySupplierEmail: quoteRequest.supplier_email
      });

      const response = await fetch(`${this.baseUrl}/quotes/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteRequest)
      });

      // Get raw response text first to handle potential JSON parsing issues
      const responseText = await response.text();
      console.log('📥 Raw backend response:', responseText);
      
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('📥 Parsed backend response:', { status: response.status, result });
      } catch (parseError) {
        console.error('❌ Failed to parse backend response as JSON:', parseError);
        return {
          success: false,
          message: `Invalid response format: ${responseText}`,
          error: 'JSON parse error'
        };
      }
      
      if (!response.ok) {
        console.error('❌ Backend error:', result);
        return {
          success: false,
          message: result.message || 'Fehler beim Erstellen der Angebotsanfrage',
          error: result.error
        };
      }

      // Validate the backend response structure
      if (!result.data || !result.data.case_id) {
        console.error('❌ Invalid backend response structure:', result);
        return {
          success: false,
          message: 'Invalid response structure from backend',
          error: 'Missing case_id in response'
        };
      }

      // Map the backend response back to frontend format
      const mappedData = {
        id: result.data.case_id,
        case_number: result.data.case_number,
        case_type: 'supplier_case',
        case_sub_type: 'quote_request',
        status: result.data.status,
        priority: 3,
        title: caseData.title,
        customer_email: primarySupplier.email,
        customer_name: primarySupplier.name,
        customer_company: primarySupplier.company,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        // Add these at top level for frontend form compatibility
        temp_order_id: result.data.temp_order_id,
        positions: Array.isArray(result.data.positions) ? result.data.positions : [],
        metadata: {
          ...caseData.metadata,
          temp_order_id: result.data.temp_order_id,
          positions: Array.isArray(result.data.positions) ? result.data.positions : []
        }
      };

      console.log('🎯 Mapped data for frontend:', mappedData);
      console.log('🔍 Position details for file uploads:');
      if (mappedData.positions && Array.isArray(mappedData.positions)) {
        mappedData.positions.forEach((pos: any, index: number) => {
          console.log(`   Position ${index}:`, {
            position_id: pos.position_id,
            position_number: pos.position_number,
            description: pos.description
          });
        });
      } else {
        console.warn('⚠️ No positions array found in response!');
      }

      return {
        success: true,
        message: 'Angebotsanfrage erfolgreich erstellt',
        data: mappedData
      };

    } catch (error: any) {
      console.error('❌ Network/unexpected error in createQuoteRequest:', error);
      return {
        success: false,
        message: error.message || 'Fehler beim Erstellen der Angebotsanfrage',
        error: error.message
      };
    }
  }

  /**
   * Send quote request email for a supplier case (supports both single and multi-supplier with attachments)
   * Uses the correct endpoint that generates the perfect email format with attachments
   */
  async sendQuoteEmail(caseId: string): Promise<ApiResponse<any>> {
    // Use the CORRECT endpoint that generates perfect email format with attachments support
    const url = `${this.baseUrl}/cases/${caseId}/send-quote-email`;
    
    try {
      console.log('📧 Sending quote email (with attachments and multi-supplier support):', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          // Email content will be generated automatically by the backend
          // Backend will detect multiple suppliers and send perfect email format to all
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        console.error('❌ Quote email sending failed:', data);
        return { success: false, message: data.message || 'Fehler beim Senden der E-Mail' };
      }

      console.log('✅ Quote email sent successfully:', data);
      
      // Handle both single and multi-supplier response formats
      if (data.data?.results) {
        // Multi-supplier response
        const sentCount = data.data.sent_count || 0;
        const totalSuppliers = data.data.total_suppliers || 0;
        return { 
          success: true, 
          data, 
          message: `Angebotsanfragen erfolgreich an ${sentCount} von ${totalSuppliers} Lieferanten versendet` 
        };
      } else {
        // Single supplier response
        return { success: true, data, message: 'Angebotsanfrage erfolgreich versendet' };
      }
    } catch (error: any) {
      console.error('❌ Network error sending quote email:', error);
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Send quote request emails to all suppliers in a case
   */
  async sendQuoteEmailToAllSuppliers(caseId: string): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/quotes/${caseId}/send-to-all-suppliers`;
    
    try {
      console.log('📧 Sending quote emails to all suppliers:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('❌ Quote bulk email sending failed:', data);
        return { 
          success: false, 
          message: data.message || 'Fehler beim Versenden der Angebotsanfragen',
          error: data.error 
        };
      }

      console.log('✅ Quote emails sent to all suppliers successfully:', data);
      return { 
        success: true, 
        data, 
        message: data.message || 'Angebotsanfragen erfolgreich an alle Lieferanten versendet' 
      };
    } catch (error: any) {
      console.error('❌ Network error sending bulk quote emails:', error);
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Get quote responses for a supplier case
   */
  async getQuoteResponses(caseId: string): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/quotes/${caseId}/responses`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, message: result.message || 'Fehler beim Laden der Angebote' };
      }

      // Extract the quote_responses array from the backend response structure
      return { 
        success: true, 
        data: {
          quote_responses: result.data?.quote_responses || [],
          case_number: result.data?.case_number,
          supplier_name: result.data?.supplier_name,
          total_responses: result.data?.total_responses || 0
        }, 
        message: 'Angebote erfolgreich geladen' 
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Evaluate quotes for a supplier case
   */
  async evaluateQuotes(caseId: string, criteria?: any): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/quotes/${caseId}/evaluate`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ criteria })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, message: data.message || 'Fehler bei der Angebotsbewertung' };
      }

      return { success: true, data, message: 'Angebote erfolgreich bewertet' };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Convert selected quote to order
   */
  async convertQuoteToOrder(caseId: string, selectedQuoteId: string, language: 'de' | 'en' = 'de'): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/quotes/${caseId}/convert-to-order`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ 
          selected_quote_id: selectedQuoteId,
          auto_generate_pdf: true,
          auto_send_email: true,
          language: language
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, message: data.message || 'Fehler bei der Umwandlung in Bestellung' };
      }

      return { 
        success: true, 
        data: {
          order_id: data.data.order_id,
          case_id: data.data.case_id,
          selected_quote_id: data.data.selected_quote_id,
          pdf_generated: data.data.pdf_generated,
          pdf_language: data.data.pdf_language,
          pdf_path: data.data.pdf_path,
          email_sent: data.data.email_sent,
          supplier_email: data.data.supplier_email
        }, 
        message: data.data.pdf_generated && data.data.email_sent
          ? `Bestellung erstellt, PDF (${data.data.pdf_language?.toUpperCase()}) generiert und an Lieferant gesendet!`
          : data.data.pdf_generated 
          ? `Bestellung erstellt und PDF (${data.data.pdf_language?.toUpperCase()}) generiert!`
          : 'Erfolgreich in Bestellung umgewandelt'
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Process quote response from email
   */
  async processQuoteResponse(caseId: string, emailId: string): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/quotes/${caseId}/process-response`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ email_id: emailId })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, message: data.message || 'Fehler bei der Verarbeitung der Antwort' };
      }

      return { success: true, data, message: 'Antwort erfolgreich verarbeitet' };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Create supplier quote request
   */
  async createSupplierQuoteRequest(requestData: any): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/quotes/initiate`;
    
    try {
      console.log('🔧 createSupplierQuoteRequest called with:', requestData);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestData)
      });

      const result = await response.json();
      console.log('📥 Backend response:', { status: response.status, result });
      
      if (!response.ok) {
        console.error('❌ Backend error:', result);
        return { success: false, message: result.message || 'Fehler beim Erstellen der Angebotsanfrage' };
      }

      // Validate the backend response structure
      if (!result.data || !result.data.case_id) {
        console.error('❌ Invalid backend response structure:', result);
        return { success: false, message: 'Invalid response from backend' };
      }

      // Map the backend response to match frontend expectations
      const mappedData = {
        id: result.data.case_id,
        case_number: result.data.case_number,
        case_type: 'supplier_case',
        case_sub_type: 'quote_request',
        status: result.data.status,
        priority: 3,
        customer_email: requestData.supplier_email,
        customer_name: requestData.supplier_name,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        // Add these at top level for file upload compatibility
        temp_order_id: result.data.temp_order_id,
        positions: result.data.positions || [],
        metadata: {
          temp_order_id: result.data.temp_order_id,
          positions: result.data.positions || [],
          order_type: requestData.order_type,
          supplier_id: requestData.supplier_id
        }
      };

      console.log('🎯 Mapped data for frontend:', mappedData);
      console.log('🔍 Position details for file uploads:');
      if (mappedData.positions && Array.isArray(mappedData.positions)) {
        mappedData.positions.forEach((pos: any, index: number) => {
          console.log(`   Position ${index}:`, {
            position_id: pos.position_id,
            position_number: pos.position_number,
            description: pos.description
          });
        });
      } else {
        console.warn('⚠️ No positions array found in response!');
      }

      return { success: true, data: mappedData, message: 'Angebotsanfrage erfolgreich erstellt' };
    } catch (error: any) {
      console.error('❌ Network error:', error);
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Upload position file - Upload a file for a specific position in an order/case
   */
  async uploadPositionFile(orderId: string, positionId: string, file: File, description?: string): Promise<ApiResponse<any>> {
    // Use the unified orders API endpoint for position file uploads
    const url = `${API_BASE_URL}/v3/orders/${orderId}/positions/${positionId}/files`;
    
    try {
      console.log('🔧 uploadPositionFile called with:', {
        orderId,
        positionId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        description,
        url
      });

      const formData = new FormData();
      formData.append('files', file);
      if (description) {
        formData.append('description', description);
      }

      console.log('📤 Sending FormData:', {
        hasFile: formData.has('files'),
        hasDescription: formData.has('description'),
        fileEntry: formData.get('files')
      });

      const response = await fetch(url, {
        method: 'POST',
        // Don't set Content-Type header - let browser set it with boundary for FormData
        headers: {
          'Accept': 'application/json'
        },
        body: formData
      });

      console.log('📥 Upload response status:', response.status);
      console.log('📥 Upload response headers:', response.headers);

      let data;
      const responseText = await response.text();
      console.log('📥 Raw response text:', responseText);

      try {
        data = JSON.parse(responseText);
        console.log('📥 Parsed response data:', data);
      } catch (parseError) {
        console.error('❌ Failed to parse response as JSON:', parseError);
        return { 
          success: false, 
          message: `Invalid response format: ${responseText}` 
        };
      }
      
      if (!response.ok) {
        console.error('❌ Upload failed with status:', response.status, data);
        return { success: false, message: data.message || 'Fehler beim Hochladen der Datei' };
      }

      console.log('✅ Upload successful:', data);
      return { success: true, data, message: 'Datei erfolgreich hochgeladen' };
    } catch (error: any) {
      console.error('❌ Upload network error:', error);
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Get position files - Get all files for a specific position
   */
  async getPositionFiles(orderId: string, positionId: string): Promise<ApiResponse<any>> {
    const url = `${API_BASE_URL}/v3/orders/${orderId}/positions/${positionId}/files`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, message: data.message || 'Fehler beim Laden der Positionsdateien' };
      }

      return { success: true, data, message: 'Positionsdateien erfolgreich geladen' };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Get case status display information
   */
  static getCaseStatusInfo(status: CaseStatus) {
    const statusConfig = {
      'new': { color: '#FF9800', label: 'Neu', description: 'Neuer Fall erstellt' },
      'open': { color: '#2196F3', label: 'Offen', description: 'Fall in Bearbeitung' },
      'in_progress': { color: '#FF5722', label: 'In Bearbeitung', description: 'Aktiv bearbeitet' },
      'pending_response': { color: '#9C27B0', label: 'Wartet auf Antwort', description: 'Wartet auf Kunden-/Lieferantenantwort' },
      'resolved': { color: '#4CAF50', label: 'Gelöst', description: 'Fall erfolgreich gelöst' },
      'closed': { color: '#757575', label: 'Geschlossen', description: 'Fall abgeschlossen' }
    };
    
    return statusConfig[status] || { color: '#757575', label: status, description: 'Unbekannter Status' };
  }

  /**
   * Get case type display information
   */
  static getCaseTypeInfo(caseType: string) {
    const typeConfig = {
      'supplier_case': { label: 'Lieferanten-Fall', description: 'Lieferantenanfrage oder Problem' },
      'support': { label: 'Support', description: 'Support-Anfrage' },
      'inquiry': { label: 'Anfrage', description: 'Kundenanfrage oder Information' },
      'general': { label: 'Allgemein', description: 'Allgemeiner Fall' },
      'standard_order': { label: 'Standard-Bestellung', description: 'Standard Bestellungsbearbeitung' },
      'customer_order': { label: 'Kundenbestellung', description: 'Kundenspezifische Bestellung' }
    };
    
    return typeConfig[caseType as keyof typeof typeConfig] || { label: caseType, description: 'Unbekannter Typ' };
  }

  /**
   * Get priority display information
   */
  static getPriorityInfo(priority: number) {
    const priorityConfig = {
      1: { color: '#F44336', label: 'Sehr Hoch', icon: '🔴', description: 'Kritisch - Sofortige Bearbeitung erforderlich' },
      2: { color: '#FF5722', label: 'Hoch', icon: '🟠', description: 'Hoch - Schnelle Bearbeitung erforderlich' },
      3: { color: '#FF9800', label: 'Normal', icon: '🟡', description: 'Normal - Standardbearbeitung' },
      4: { color: '#4CAF50', label: 'Niedrig', icon: '🟢', description: 'Niedrig - Kann später bearbeitet werden' },
      5: { color: '#2196F3', label: 'Sehr Niedrig', icon: '🔵', description: 'Sehr niedrig - Keine Eile' }
    };
    
    return priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig[3];
  }

  /**
   * Format date for display
   */
  static formatDate(dateString: string, includeTime: boolean = true): string {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };

    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }

    return date.toLocaleString('de-DE', options);
  }

  /**
   * Calculate time ago for relative timestamps
   */
  static getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    } else if (diffHours > 0) {
      return `vor ${diffHours} Stunde${diffHours > 1 ? 'n' : ''}`;
    } else if (diffMinutes > 0) {
      return `vor ${diffMinutes} Minute${diffMinutes > 1 ? 'n' : ''}`;
    } else {
      return 'gerade eben';
    }
  }

  /**
   * Get headers for API requests
   */
  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      // Add any other common headers here, like authorization
    };
  }

  /**
   * Get case emails - Fetch all emails related to a specific case
   */
  async getCaseEmails(caseId: string): Promise<ApiResponse<any[]>> {
    const url = `${this.baseUrl}/cases/${caseId}/emails`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      const result = await response.json();
      
      if (!response.ok) {
        return { success: false, message: result.message || 'Fehler beim Laden der E-Mails' };
      }

      // Extract the emails array from the backend response structure
      return { 
        success: true, 
        data: result.data?.emails || [], 
        message: 'E-Mails erfolgreich geladen' 
      };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Send case email - Send a new email related to a case
   */
  async sendCaseEmail(caseId: string, emailData: {
    to: string;
    subject: string;
    body: string;
    cc?: string;
    bcc?: string;
    in_reply_to?: string;
  }): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/cases/${caseId}/send-email`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(emailData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, message: data.message || 'Fehler beim Senden der E-Mail' };
      }

      return { success: true, data, message: 'E-Mail erfolgreich gesendet' };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Mark email as read
   */
  async markEmailAsRead(emailId: string): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/emails/${emailId}/read`;
    
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, message: data.message || 'Fehler beim Markieren der E-Mail' };
      }

      return { success: true, data, message: 'E-Mail als gelesen markiert' };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }

  /**
   * Map supplier quote to customer position
   */
  async mapSupplierQuoteToPosition(
    positionId: string, 
    mappingData: {
      supplier_case_id: string;
      supplier_position_number: number;
      quoted_price: number;
      delivery_time_days?: number | null;
      supplier_notes?: string;
    }
  ): Promise<ApiResponse<any>> {
    const url = `${this.baseUrl}/cases/positions/${positionId}/map-supplier-quote`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(mappingData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return { success: false, message: data.message || 'Fehler beim Mapping der Supplier Quote' };
      }

      return { success: true, data, message: 'Supplier Quote erfolgreich gemappt' };
    } catch (error: any) {
      return { success: false, message: `Netzwerkfehler: ${error.message}` };
    }
  }
}

// Create singleton instance
export const caseManagementService = new CaseManagementService();
export default caseManagementService;
